import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSandboxRuntimeLogs } from '../../hooks/use-sandbox-runtime-logs';
import { ApiError } from '../../lib/api-client';
import { useApiClient } from '../../providers/auth-provider';
import { formatDuration, relativeTime } from '../skills/skill-invocation-helpers';

import { RunStatusBadge } from './run-status';
import { TraceWaterfall } from './TraceWaterfall';

import type {
  SandboxLogRecord,
  SandboxRuntimeDetailResponse,
  SandboxRuntimeTraceResponse,
} from '../../lib/api-client';
import type { SandboxLogEntry } from '../../hooks/use-sandbox-runtime-logs';

type Tab = 'logs' | 'details' | 'traces';

const STREAM_COLORS: Record<string, string> = {
  stderr: 'var(--danger, #d64545)',
  logger: 'var(--accent, #4a7dd6)',
  tap: 'var(--warning, #c98a2b)',
};

function timeOf(entry: SandboxLogRecord): string {
  if (typeof entry.ts !== 'number') return '';
  return new Date(entry.ts * 1000).toISOString().slice(11, 23);
}

/**
 * Session detail (kube-dashboard style): a rich header — never a raw log
 * dump — then Logs / Details / Traces tabs. Logs stay pinned to the bottom
 * with live follow while the session is alive and page older history on
 * scroll-up; Details carries everything we know plus the per-run table;
 * Traces renders the SigNoz-style waterfall per run.
 */
export function SandboxRuntimeDetailView({
  podSessionId,
  tenantId,
}: {
  podSessionId?: string;
  /** The session's tenant (may be a descendant — rows come from scope=subtree). */
  tenantId?: string;
}) {
  const apiClient = useApiClient();
  const [detail, setDetail] = useState<SandboxRuntimeDetailResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | undefined>();
  const [tab, setTab] = useState<Tab>('logs');
  const [focusTraceId, setFocusTraceId] = useState<string | null>(null);
  const inFlight = useRef(false);

  const fetchDetail = useCallback(() => {
    if (!podSessionId || inFlight.current) return;
    inFlight.current = true;
    apiClient
      .getSandboxRuntime(podSessionId, { tenantId })
      .then((res) => {
        setDetail(res);
        setStatus('success');
        setError(undefined);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load session');
        setStatus((prev) => (prev === 'success' ? prev : 'error'));
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [apiClient, podSessionId, tenantId]);

  useEffect(() => {
    setDetail(null);
    setStatus('loading');
    fetchDetail();
  }, [fetchDetail]);

  // Header/runs refresh while the session is live (or a run is in flight).
  const active = !!detail && (detail.live || detail.last_status === 'running');
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchDetail();
    }, 10_000);
    return () => clearInterval(id);
  }, [active, fetchDetail]);

  if (!podSessionId) {
    return <Centered>No session selected.</Centered>;
  }
  if (status === 'loading' && !detail) {
    return <Centered>Loading session…</Centered>;
  }
  if (status === 'error' && !detail) {
    return <Centered error>{error ?? 'Failed to load session.'}</Centered>;
  }
  if (!detail) return null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <Header detail={detail} />
      <TabBar tab={tab} onSelect={setTab} live={detail.live} />
      {tab === 'logs' && <LogsTab podSessionId={podSessionId} tenantId={tenantId} live={detail.live} />}
      {tab === 'details' && (
        <DetailsTab
          detail={detail}
          onViewTrace={(traceId) => {
            setFocusTraceId(traceId);
            setTab('traces');
          }}
        />
      )}
      {tab === 'traces' && (
        <TracesTab
          podSessionId={podSessionId}
          tenantId={tenantId}
          detail={detail}
          focusTraceId={focusTraceId}
        />
      )}
    </div>
  );
}

function Header({ detail }: { detail: SandboxRuntimeDetailResponse }) {
  return (
    <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {detail.live ? (
          <span
            title="Live — holds a warm sandbox lease"
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: 'var(--kind-success, #2da44e)',
              animation: 'sd-pulse 1.6s ease-in-out infinite',
              flexShrink: 0,
            }}
          />
        ) : null}
        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--text)',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }}
        >
          {detail.pod_session_id}
        </span>
        {detail.last_status ? <RunStatusBadge status={detail.last_status} /> : null}
        <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {detail.live ? 'live' : 'history'}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          marginTop: 8,
          fontSize: 11,
          color: 'var(--text-soft)',
        }}
      >
        <HeaderStat label="Sandbox" value={detail.sandbox_name ?? '—'} mono />
        <HeaderStat
          label="Tenant"
          value={detail.tenant_external_id ?? detail.tenant_id}
          title={detail.tenant_id}
          mono
        />
        <HeaderStat
          label="User"
          value={detail.user_external_id ?? detail.user_email ?? detail.user_id ?? '—'}
          title={detail.user_id ?? undefined}
          mono
        />
        <HeaderStat
          label="Started"
          value={detail.first_started_at ? relativeTime(detail.first_started_at) : '—'}
        />
        <HeaderStat label="Duration" value={formatDuration(detail.duration_ms)} />
        <HeaderStat label="Runs" value={String(detail.runs_count)} />
        {detail.lease_expires_at ? (
          <HeaderStat label="Lease expires" value={relativeTime(detail.lease_expires_at)} />
        ) : null}
      </div>
    </div>
  );
}

function HeaderStat({
  label,
  value,
  title,
  mono = false,
}: {
  label: string;
  value: string;
  title?: string | undefined;
  mono?: boolean;
}) {
  return (
    <span title={title} style={{ display: 'inline-flex', gap: 5, alignItems: 'baseline', minWidth: 0 }}>
      <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-mute)' }}>
        {label}
      </span>
      <span
        style={{
          color: 'var(--text)',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
          maxWidth: 240,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </span>
  );
}

function TabBar({ tab, onSelect, live }: { tab: Tab; onSelect: (t: Tab) => void; live: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        flexShrink: 0,
      }}
      role="tablist"
    >
      <TabButton active={tab === 'logs'} onClick={() => onSelect('logs')}>
        Logs
        {live ? (
          <span
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--kind-success, #2da44e)',
              marginLeft: 6,
              animation: 'sd-pulse 1.6s ease-in-out infinite',
            }}
          />
        ) : null}
      </TabButton>
      <TabButton active={tab === 'details'} onClick={() => onSelect('details')}>
        Details
      </TabButton>
      <TabButton active={tab === 'traces'} onClick={() => onSelect('traces')}>
        Traces
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: '10px 16px',
        fontSize: 12,
        fontWeight: 600,
        color: active ? 'var(--accent)' : 'var(--text-mute)',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

/** Scroll distance from the top that triggers an older-history page load. */
const LOAD_OLDER_THRESHOLD_PX = 80;

function LogsTab({
  podSessionId,
  tenantId,
  live,
}: {
  podSessionId: string;
  tenantId?: string;
  live: boolean;
}) {
  const [follow, setFollow] = useState(live);
  const [grep, setGrep] = useState('');
  const [grepDraft, setGrepDraft] = useState('');
  const [streamFilter, setStreamFilter] = useState<'' | 'stdout' | 'stderr' | 'logger' | 'tap'>('');

  // A session that comes alive mid-view starts following; one that dies stops.
  useEffect(() => setFollow(live), [live]);

  const filters = useMemo(
    () => ({
      follow: follow && live,
      ...(grep ? { grep } : {}),
      ...(streamFilter ? { stream: streamFilter } : {}),
    }),
    [follow, live, grep, streamFilter],
  );
  const { entries, status, error, hasOlder, loadingOlder, loadOlder } = useSandboxRuntimeLogs(
    podSessionId,
    filters,
    tenantId,
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);

  // Pin to bottom: on new entries, keep the newest line visible unless the
  // user has scrolled up into history.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [entries]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (el.scrollTop < LOAD_OLDER_THRESHOLD_PX && hasOlder && !loadingOlder) {
      // Anchor the viewport across the prepend: measure, load, restore.
      const prevHeight = el.scrollHeight;
      const prevTop = el.scrollTop;
      void loadOlder().then((added) => {
        if (added > 0) {
          requestAnimationFrame(() => {
            const node = scrollRef.current;
            if (node) node.scrollTop = node.scrollHeight - prevHeight + prevTop;
          });
        }
      });
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>
          {status === 'streaming' ? (filters.follow ? 'live' : 'replaying…') : status}
          {' · '}
          {entries.length} lines
        </span>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setGrep(grepDraft);
          }}
          style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}
        >
          <input
            placeholder="grep (server-side regex)…"
            value={grepDraft}
            onChange={(e) => setGrepDraft(e.target.value)}
            style={{
              padding: '4px 8px',
              fontSize: 11,
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              background: 'var(--surface)',
              color: 'var(--text)',
              outline: 'none',
              fontFamily: 'inherit',
              minWidth: 180,
            }}
          />
        </form>
        <select
          value={streamFilter}
          onChange={(e) => setStreamFilter(e.target.value as typeof streamFilter)}
          style={{
            padding: '4px 6px',
            fontSize: 11,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface)',
            color: 'var(--text)',
          }}
        >
          <option value="">all streams</option>
          <option value="stdout">stdout</option>
          <option value="stderr">stderr</option>
          <option value="logger">logger</option>
          <option value="tap">tap (gaps)</option>
        </select>
        {live ? (
          <label
            style={{
              fontSize: 11,
              color: 'var(--text)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
            follow
          </label>
        ) : null}
      </div>
      {status === 'error' ? (
        <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--danger, #d64545)' }}>
          {error}
        </div>
      ) : null}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          flex: 1,
          overflow: 'auto',
          background: 'var(--surface-sunken, var(--surface))',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 11.5,
          lineHeight: 1.55,
          padding: '8px 12px',
        }}
      >
        {loadingOlder ? (
          <div style={{ color: 'var(--text-mute)', fontStyle: 'italic' }}>loading older…</div>
        ) : null}
        {hasOlder === false && entries.length > 0 ? (
          <div style={{ color: 'var(--text-mute)', fontStyle: 'italic' }}>
            — start of retained history —
          </div>
        ) : null}
        {entries.map((entry: SandboxLogEntry) =>
          entry.type === 'gap' ? (
            <div key={entry.key} style={{ color: STREAM_COLORS['tap'], fontStyle: 'italic' }}>
              ··· {entry.dropped ?? '?'} lines dropped (quota/backpressure) ···
            </div>
          ) : (
            <div key={entry.key} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <span style={{ color: 'var(--text-soft)' }}>{timeOf(entry)} </span>
              <span
                style={{
                  color: STREAM_COLORS[entry.stream ?? ''] ?? 'var(--text-soft)',
                  fontWeight: 600,
                }}
              >
                [{entry.stream ?? '?'}]{' '}
              </span>
              <span style={{ color: 'var(--text)' }}>{entry.line ?? ''}</span>
            </div>
          ),
        )}
        {entries.length === 0 && status !== 'streaming' ? (
          <div style={{ color: 'var(--text-mute)', fontStyle: 'italic' }}>
            No log records retained for this session.
          </div>
        ) : null}
        {status === 'ended' && !filters.follow ? (
          <div style={{ color: 'var(--text-soft)', fontStyle: 'italic', marginTop: 4 }}>
            — end of retained history —
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DetailsTab({
  detail,
  onViewTrace,
}: {
  detail: SandboxRuntimeDetailResponse;
  onViewTrace: (traceId: string) => void;
}) {
  const fields: Array<[string, string | null]> = [
    ['Session (pod_session_id)', detail.pod_session_id],
    ['Sandbox pod', detail.sandbox_name],
    ['Claim', detail.claim_name],
    ['Agent type', detail.agent_type],
    ['Tenant', detail.tenant_id],
    ['Tenant external id', detail.tenant_external_id],
    ['User', detail.user_id],
    ['User external id', detail.user_external_id],
    ['User email', detail.user_email],
    ['Conversation', detail.conversation_id],
    ['Conversation external id', detail.conversation_external_id],
    ['First started', detail.first_started_at],
    ['Last activity', detail.last_activity_at],
    ['Duration', formatDuration(detail.duration_ms)],
    ['Lease expires', detail.lease_expires_at],
  ];
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '10px 24px',
          padding: '16px 20px',
        }}
      >
        {fields.map(([label, value]) => (
          <div key={label} style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 9,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'var(--text-mute)',
                marginBottom: 2,
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--text)',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={value ?? undefined}
            >
              {value ?? '—'}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '4px 20px 8px', fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
        Runs ({detail.runs.length})
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
        <thead>
          <tr
            style={{
              background: 'var(--bg-alt)',
              textAlign: 'left',
              color: 'var(--text-soft)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            <th style={{ padding: '7px 12px 7px 20px', fontWeight: 600 }}>Started</th>
            <th style={{ padding: '7px 12px', fontWeight: 600 }}>Duration</th>
            <th style={{ padding: '7px 12px', fontWeight: 600 }}>Status</th>
            <th style={{ padding: '7px 12px', fontWeight: 600 }}>Bundle</th>
            <th style={{ padding: '7px 12px', fontWeight: 600 }}>SDKs</th>
            <th style={{ padding: '7px 12px', fontWeight: 600 }}>Job</th>
            <th style={{ padding: '7px 12px', fontWeight: 600 }}>Trace</th>
          </tr>
        </thead>
        <tbody>
          {detail.runs.map((run) => (
            <tr key={run.id} style={{ borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
              <td style={{ padding: '8px 12px 8px 20px', whiteSpace: 'nowrap' }}>
                {relativeTime(run.started_at)}
              </td>
              <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                {formatDuration(run.duration_ms)}
              </td>
              <td style={{ padding: '8px 12px' }}>
                <RunStatusBadge status={run.status} />
                {run.error_message ? (
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--kind-failure)',
                      maxWidth: 420,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={run.error_message}
                  >
                    {run.error_message}
                  </div>
                ) : null}
              </td>
              <td
                style={{
                  padding: '8px 12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontSize: 10.5,
                  maxWidth: 220,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={run.sandbox_image ? `${run.sandbox_image}\npool: ${run.pool_name ?? '—'}` : undefined}
              >
                {run.sandbox_image ? (run.sandbox_image.split('/').pop() ?? run.sandbox_image) : '—'}
              </td>
              <td
                style={{
                  padding: '8px 12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontSize: 10.5,
                  whiteSpace: 'nowrap',
                }}
                title="client / worker SDK versions on this run"
              >
                {run.client_sdk_version ?? '—'} / {run.worker_sdk_version ?? '—'}
              </td>
              <td
                style={{
                  padding: '8px 12px',
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontSize: 10.5,
                  maxWidth: 220,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={run.job_id}
              >
                {run.job_id}
              </td>
              <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                {run.trace_id ? (
                  <button
                    type="button"
                    onClick={() => onViewTrace(run.trace_id!)}
                    style={{
                      padding: '2px 8px',
                      fontSize: 10.5,
                      border: '1px solid var(--border-strong)',
                      borderRadius: 5,
                      background: 'var(--surface)',
                      color: 'var(--accent)',
                      cursor: 'pointer',
                      fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    }}
                    title={run.trace_id}
                  >
                    {run.trace_id.slice(0, 12)}… →
                  </button>
                ) : (
                  <span style={{ color: 'var(--text-mute)' }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {detail.runs.length === 0 ? (
        <div style={{ padding: 20, fontSize: 12, color: 'var(--text-mute)' }}>
          No recorded runs — this session predates run history.
        </div>
      ) : null}
    </div>
  );
}

function TracesTab({
  podSessionId,
  tenantId,
  detail,
  focusTraceId,
}: {
  podSessionId: string;
  tenantId?: string;
  detail: SandboxRuntimeDetailResponse;
  focusTraceId: string | null;
}) {
  const apiClient = useApiClient();
  const traced = useMemo(
    () => detail.runs.filter((r) => r.trace_id !== null),
    [detail.runs],
  );
  const [traceId, setTraceId] = useState<string | null>(
    focusTraceId ?? traced[0]?.trace_id ?? null,
  );
  useEffect(() => {
    if (focusTraceId) setTraceId(focusTraceId);
  }, [focusTraceId]);
  const [trace, setTrace] = useState<SandboxRuntimeTraceResponse | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!traceId) {
      setStatus('idle');
      setTrace(null);
      return;
    }
    let cancelled = false;
    setStatus('loading');
    apiClient
      .getSandboxRuntimeTrace(podSessionId, traceId, { tenantId })
      .then((res) => {
        if (cancelled) return;
        setTrace(res);
        setStatus('success');
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load trace',
        );
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, podSessionId, traceId]);

  if (traced.length === 0) {
    return (
      <Centered>
        No traced runs on this session — trace ids attach to runs recorded after the sandbox_runs
        rollout.
      </Centered>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>Run:</span>
        <select
          value={traceId ?? ''}
          onChange={(e) => setTraceId(e.target.value || null)}
          style={{
            padding: '4px 6px',
            fontSize: 11,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            background: 'var(--surface)',
            color: 'var(--text)',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            maxWidth: 420,
          }}
        >
          {traced.map((run) => (
            <option key={run.id} value={run.trace_id!}>
              {relativeTime(run.started_at)} · {run.status} · {run.trace_id!.slice(0, 16)}…
            </option>
          ))}
        </select>
        {trace?.signoz_url ? (
          <a
            href={trace.signoz_url}
            target="_blank"
            rel="noreferrer"
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              color: 'var(--accent)',
              textDecoration: 'none',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              padding: '4px 10px',
            }}
          >
            Open in SigNoz ↗
          </a>
        ) : null}
      </div>
      {status === 'loading' ? <Centered>Loading trace…</Centered> : null}
      {status === 'error' ? <Centered error>{error}</Centered> : null}
      {status === 'success' && trace ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <TraceWaterfall spans={trace.spans} />
        </div>
      ) : null}
    </div>
  );
}

function Centered({ children, error = false }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: error ? 'var(--kind-failure)' : 'var(--text-mute)',
        fontSize: 12,
        padding: 24,
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}
